import React, { useState, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert, Sparkles, Loader2, CheckCircle } from 'lucide-react';
import { AuthContext, TasksContext } from '@/App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClashReviewPanel = ({ clashes, onDone, onKeep }) => {
  const { auth } = useContext(AuthContext);
  const { bumpTasks } = useContext(TasksContext);
  const [state, setState] = useState('idle');
  const [results, setResults] = useState([]);

  const moveAll = async () => {
    setState('saving');
    try {
      const res = await axios.post(`${API}/clashes/resolve`, {
        task_ids: clashes.map(c => c.task_id), tz_offset_minutes: -new Date().getTimezoneOffset(),
      }, { headers: { Authorization: `Bearer ${auth.token}` } });
      setResults(res.data.results || []);
      setState('done');
      toast.success(`Moved ${res.data.moved} task(s)${res.data.pending ? `, ${res.data.pending} sent to Pending` : ''}`);
      bumpTasks();
      onDone?.(res.data.results || []);
    } catch {
      setState('idle');
      toast.error('Could not move the tasks');
    }
  };

  return (
    <div className="glass-card p-5 border border-amber-400/25" data-testid="clash-review-panel">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert size={16} className="text-amber-300" />
        <h3 className="text-sm font-semibold text-white">
          {state === 'done' ? 'Protected time is clear again' : `${clashes.length} task${clashes.length !== 1 ? 's' : ''} clash with your protected time`}
        </h3>
      </div>
      {state !== 'done' ? (
        <ul className="space-y-1.5 mb-4" data-testid="clash-review-list">
          {clashes.map(c => (
            <li key={c.task_id} className="text-sm text-gray-300" data-testid={`clash-item-${c.task_id}`}>
              <span className="text-white">{c.title}</span>, {c.local}
              <span className="text-amber-300/80 text-xs"> · clashes with {c.clashes_with.join(', ')}</span>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="space-y-2 mb-2" data-testid="clash-results-list">
          {results.map(r => (
            <li key={r.task_id} className="text-sm" data-testid={`clash-result-${r.task_id}`}>
              <span className="flex items-center gap-1.5 text-white">
                <CheckCircle size={13} className={r.action === 'moved' ? 'text-cyan-300' : 'text-amber-300'} />
                {r.title} → {r.action === 'moved' ? r.local : 'Pending'}
              </span>
              <span className="block text-xs text-gray-500 ml-5">{r.reasons.join(' · ')}</span>
            </li>
          ))}
        </ul>
      )}
      {state === 'done' && (
        <button onClick={onKeep} data-testid="clash-close-btn"
          className="mt-2 px-4 py-2 rounded-lg text-sm text-gray-300 border border-white/10 hover:text-white transition-colors">
          Close
        </button>
      )}
      {state !== 'done' && (
        <div className="flex gap-2">
          <button onClick={moveAll} disabled={state === 'saving'} data-testid="clash-move-all-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-[#0A0E27] disabled:opacity-60 transition-transform hover:scale-[1.02]"
            style={{ background: 'linear-gradient(135deg,#00D9FF,#0EA5E9)' }}>
            {state === 'saving' ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Move these with AI
          </button>
          <button onClick={onKeep} data-testid="clash-keep-btn"
            className="px-4 py-2 rounded-lg text-sm text-gray-300 border border-white/10 hover:text-white transition-colors">
            Keep as is
          </button>
        </div>
      )}
    </div>
  );
};

export default ClashReviewPanel;
